import { useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { openPortal, daysUntil, formatDate } from '../lib/billingApi';

const SEEN_KEY = 'wp.billingNotice';

/**
 * Once per browser session, when the editor opens: a past-due card gets a warning with a shortcut to Stripe; a trial
 * in its last three days gets a reminder of when the first charge happens.
 */
export const useBillingNotices = () => {
  const { user } = useAuth();
  const status = user?.billing?.status;
  const trialEnd = user?.billing?.trial_end;
  const cancelling = !!user?.billing?.cancel_at_period_end;
  useEffect(() => {
    if (!user || !status) return;
    let seen = '';
    try { seen = window.sessionStorage.getItem(SEEN_KEY) || ''; } catch { /* ignore */ }
    const key = `${user.user_id}:${status}:${trialEnd || ''}`;
    if (seen === key) return;
    const remember = () => { try { window.sessionStorage.setItem(SEEN_KEY, key); } catch { /* ignore */ } };
    if (status === 'past_due') {
      remember();
      toast.warning('Payment problem', {
        id: 'billing-notice', duration: 12000,
        description: 'Stripe could not charge your card. Update it to keep your access — your publications are safe either way.',
        action: { label: 'Update card', onClick: async () => { try { const { url } = await openPortal(); window.location.assign(url); } catch { toast.error('The billing portal could not be opened.'); } } },
      });
    } else if (status === 'trialing' && trialEnd && !cancelling && daysUntil(trialEnd) <= 3) {
      remember();
      const n = daysUntil(trialEnd);
      toast('Your free trial ends soon', { id: 'billing-notice', duration: 10000, description: `${n === 0 ? 'Today' : n === 1 ? 'Tomorrow' : `On ${formatDate(trialEnd)}`} your plan starts and your card is charged. Cancel before then from Account ▸ Billing & plan if you do not want to continue.` });
    }
  }, [user, status, trialEnd, cancelling]);
};
